import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type PlatformChromeMode = 'platform' | 'neutral';

type PlatformChromeContextValue = {
  mode: PlatformChromeMode;
  organizerName: string | null;
  setNeutralChrome: (organizerName?: string | null) => void;
  resetChrome: () => void;
};

const PlatformChromeContext = createContext<PlatformChromeContextValue>({
  mode: 'platform',
  organizerName: null,
  setNeutralChrome: () => undefined,
  resetChrome: () => undefined,
});

export function PlatformChromeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<PlatformChromeMode>('platform');
  const [organizerName, setOrganizerName] = useState<string | null>(null);

  const setNeutralChrome = useCallback((name?: string | null) => {
    setMode('neutral');
    setOrganizerName(name?.trim() || null);
  }, []);

  const resetChrome = useCallback(() => {
    setMode('platform');
    setOrganizerName(null);
  }, []);

  const value = useMemo(
    () => ({ mode, organizerName, setNeutralChrome, resetChrome }),
    [mode, organizerName, setNeutralChrome, resetChrome]
  );

  return <PlatformChromeContext.Provider value={value}>{children}</PlatformChromeContext.Provider>;
}

export function usePlatformChrome() {
  return useContext(PlatformChromeContext);
}
